import { motion } from "framer-motion";
import { Briefcase, Check, MessageCircle, Users } from "lucide-react";

const FALLBACK_IMG =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800"><rect width="1200" height="800" fill="#13191E"/><rect x="24" y="24" width="1152" height="752" fill="none" stroke="#C5A059" stroke-opacity="0.35"/><text x="600" y="400" text-anchor="middle" fill="#9CA3AF" font-family="monospace" font-size="22" letter-spacing="6">PHOTO FORTHCOMING</text></svg>`
  );

export default function FleetCard({ car, index }) {
  return (
    <motion.article
      id={car.id}
      data-testid={`fleet-card-${car.id}`}
      initial={{ opacity: 0, y: 56 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 2) * 0.14, ease: [0.16, 1, 0.3, 1] }}
      className="group relative flex scroll-mt-28 flex-col border border-line bg-surface transition-colors duration-500 hover:border-champagne/40"
    >
      <div className="spotlight-frame relative aspect-[3/2] overflow-hidden">
        <img
          src={car.image}
          alt={car.name}
          loading="lazy"
          data-testid={`fleet-image-${car.id}`}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_IMG;
          }}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
        <span
          data-testid={`fleet-badge-${car.id}`}
          className="absolute left-4 top-4 z-10 border border-champagne/40 bg-onyx/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-champagne backdrop-blur-md"
        >
          {car.badge}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
          {String(index + 1).padStart(2, "0")} — {car.tagline}
        </p>
        <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-bone sm:text-3xl">
          {car.name}
        </h3>

        <div className="mt-5 flex flex-wrap gap-3" data-testid={`fleet-specs-${car.id}`}>
          <span className="flex items-center gap-2 border border-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-smoke">
            <Users className="h-3.5 w-3.5 text-champagne" />
            {car.passengers}
          </span>
          <span className="flex items-center gap-2 border border-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-smoke">
            <Briefcase className="h-3.5 w-3.5 text-champagne" />
            {car.luggage}
          </span>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-smoke">{car.description}</p>

        <ul className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-line pt-5 sm:grid-cols-2">
          {car.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-xs text-bone/80">
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-champagne" />
              {h}
            </li>
          ))}
        </ul>

        <a
          href={`https://wa.me/14158885550?text=${encodeURIComponent(
            `Hi, I'd like a quote for the ${car.name}.`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`fleet-quote-${car.id}`}
          className="mt-6 inline-flex items-center gap-2 self-start border border-champagne/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-onyx"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Quote this vehicle
        </a>
      </div>
    </motion.article>
  );
}
